import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, FileCheck2, Receipt } from "lucide-react";
import { toast } from "sonner";

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders/mine/invoices").then((r) => {
      setInvoices(r.data.invoices);
      setLoading(false);
    });
  }, []);

  const download = async (inv) => {
    try {
      const token = localStorage.getItem("w2e_token");
      const res = await fetch(`${API_BASE}/orders/${inv.order_id}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.order_number || "invoice"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download invoice");
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="section-label">Billing</div>
        <h1 className="mt-1 font-display font-bold text-3xl text-[#0A192F]" data-testid="invoices-heading">My Invoices</h1>
        <p className="text-slate-600 mt-1 text-sm">GST-compliant tax invoices for all your paid orders.</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 text-slate-500 text-sm">Loading…</div>
        ) : invoices.length === 0 ? (
          <div className="p-10 text-center">
            <Receipt className="w-8 h-8 mx-auto text-slate-400" />
            <div className="mt-3 font-medium text-slate-700">No invoices yet</div>
            <div className="text-sm text-slate-500 mt-1">Once a payment is completed, your GST invoice will appear here.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Invoice</th>
                  <th className="text-left px-4 py-3">Service</th>
                  <th className="text-left px-4 py-3">Issued</th>
                  <th className="text-left px-4 py-3">Payment ID</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.map((i) => (
                  <tr key={i.order_id} className="hover:bg-slate-50" data-testid={`invoice-row-${i.order_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-mono font-semibold text-[#0A192F]">INV-{i.order_number}</div>
                      <div className="text-xs text-slate-500 font-mono">Order {i.order_number}</div>
                    </td>
                    <td className="px-4 py-3">{i.service_name}</td>
                    <td className="px-4 py-3 text-slate-600">{new Date(i.issued_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{i.payment_id || "—"}</td>
                    <td className="px-4 py-3 text-right font-medium">₹{i.amount}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" onClick={() => download(i)} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid={`invoice-dl-btn-${i.order_id}`}>
                        <Download className="w-3.5 h-3.5 mr-1" /> PDF
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-6 rounded-xl bg-blue-50 border border-blue-200 p-5 flex items-start gap-3">
        <FileCheck2 className="w-5 h-5 text-[#1E3A8A] shrink-0 mt-0.5" />
        <div className="text-sm text-slate-700">
          Every invoice includes seller <span className="font-medium">SHREE ADARSH INFOTECH</span>, GSTIN <span className="font-mono">08KKOPS0455P1Z2</span>, HSN/SAC code, and a CGST + SGST GST breakdown at 18%.
        </div>
      </div>
    </DashboardLayout>
  );
}
